/**
 * Documents tab of the school management screen (ADM-8): the school's private
 * knowledge documents with upload and delete. State and actions come from
 * useSchoolDocuments.
 */
import React from 'react';
import { ActivityIndicator, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { brand } from '../../../design-system/theme/tokens';
import { useTranslation } from '../../../hooks/useTranslation';
import type { SchoolFramework } from '../../../types/schoolFramework';
import { isSchoolAdmin, type User } from '../../../utils/roleUtils';
import { COLORS, type SchoolDocument } from '../schoolManagementTypes';
import { documentMethodLabel, formatFileSize, frameworkNamesById } from '../schoolRoster';
import type { SchoolDocumentsState } from '../useSchoolDocuments';
import { PrivateKnowledgeLocked } from './PrivateKnowledgeLocked';

export function DocumentsTab({
  user,
  canAccessPrivateSchoolKnowledge,
  frameworks,
  documentsState,
}: {
  user?: User;
  canAccessPrivateSchoolKnowledge: boolean;
  /** Names shown for each document's linked frameworks. */
  frameworks: SchoolFramework[];
  documentsState: SchoolDocumentsState;
}) {
  const { t, language } = useTranslation();
  const {
    documents,
    loadingDocuments,
    uploadingDocument,
    deletingDocumentId,
    handleUploadDocument,
    handleDeleteDocument,
  } = documentsState;

  const frameworkNameById = React.useMemo(
    () => frameworkNamesById(frameworks, language),
    [frameworks, language],
  );

  const getDocumentMethodLabel = React.useCallback(
    (doc: SchoolDocument): string => documentMethodLabel(doc, frameworkNameById, t),
    [frameworkNameById, t]
  );

  if (!canAccessPrivateSchoolKnowledge) {
    return <PrivateKnowledgeLocked />;
  }

  return (
    <View className="mb-6">
      {/* Header with Upload Button */}
      <View className="flex-row justify-between items-center mb-4">
        <View>
          <Text className="text-xl font-bold text-foreground">
            {t('schoolManagement.documentsSection.title')}
          </Text>
          <Text className="text-sm text-muted-foreground mt-1">
            {t('schoolManagement.documentsSection.subtitle')}
          </Text>
        </View>
        {isSchoolAdmin(user) && (
          <Button
            variant="default"
            size="sm"
            onPress={handleUploadDocument}
            disabled={uploadingDocument}
          >
            {uploadingDocument ? (
              <>
                <ActivityIndicator size="small" color="white" />
                <Text className="text-white text-xs ml-2">
                  {t('common.loading')}
                </Text>
              </>
            ) : (
              <>
                <MaterialIcons name="cloud-upload" size={16} color="white" />
                <Text className="text-white text-xs ml-2">
                  {t('schoolManagement.quickActionsButtons.uploadDocument')}
                </Text>
              </>
            )}
          </Button>
        )}
      </View>

      {/* Info Card */}
      <Card className="mb-4 bg-blue-50">
        <CardContent className="p-4">
          <View className="flex-row items-start">
            <MaterialIcons name="info" size={20} color={brand.primary} />
            <View className="flex-1 ml-3">
              <Text className="text-sm text-gray-700 font-medium mb-1">
                {t('schoolManagement.documentsSection.aboutTitle')}
              </Text>
              <Text className="text-xs text-gray-600">
                {t('schoolManagement.documentsSection.aboutDescription')}
              </Text>
            </View>
          </View>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loadingDocuments ? (
        <Card>
          <CardContent className="p-8 items-center">
            <ActivityIndicator size="large" color={COLORS.primary} />
            <Text className="text-muted-foreground mt-4">
              {t('schoolManagement.documentsSection.loading')}
            </Text>
          </CardContent>
        </Card>
      ) : documents.length === 0 ? (
        /* Empty State */
        <Card>
          <CardContent className="p-8 items-center">
            <MaterialIcons name="folder-open" size={64} color="#9CA3AF" />
            <Text className="text-lg font-bold text-foreground mt-4 mb-2">
              {t('schoolManagement.documentsSection.emptyTitle')}
            </Text>
            <Text className="text-sm text-muted-foreground text-center mb-4">
              {t('schoolManagement.documentsSection.emptySubtitle')}
            </Text>
            {isSchoolAdmin(user) && (
              <Button variant="default" size="sm" onPress={handleUploadDocument}>
                <MaterialIcons name="cloud-upload" size={16} color="white" />
                <Text className="text-white ml-2">
                  {t('schoolManagement.documentsSection.uploadFirst')}
                </Text>
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Document List */
        <View className="gap-3">
          {documents.map((doc) => (
            <Card key={doc.id}>
              <CardContent className="p-4">
                <View className="flex-row items-start justify-between">
                  <View className="flex-1">
                    {/* File Icon and Name */}
                    <View className="flex-row items-center mb-2">
                      <MaterialIcons
                        name={
                          doc.content_type === 'application/pdf'
                            ? 'picture-as-pdf'
                            : 'description'
                        }
                        size={24}
                        color={COLORS.primary}
                      />
                      <Text className="text-base font-semibold text-foreground ml-2 flex-1">
                        {doc.original_filename}
                      </Text>
                    </View>

                    {/* Document Info */}
                    <View className="flex-row flex-wrap gap-3 mb-2">
                      <View className="flex-row items-center">
                        <MaterialIcons name="category" size={14} color="#6b7280" />
                        <Text className="text-xs text-muted-foreground ml-1">
                          {t('schoolManagement.documentsSection.methodLabel')}: {getDocumentMethodLabel(doc)}
                        </Text>
                      </View>
                      <View className="flex-row items-center">
                        <MaterialIcons name="storage" size={14} color="#6b7280" />
                        <Text className="text-xs text-muted-foreground ml-1">
                          {t('schoolManagement.documentsSection.sizeLabel')}: {formatFileSize(doc.file_size)}
                        </Text>
                      </View>
                      <View className="flex-row items-center">
                        <MaterialIcons name="grid-view" size={14} color="#6b7280" />
                        <Text className="text-xs text-muted-foreground ml-1">
                          {t('schoolManagement.documentsSection.chunksLabel', {
                            count: doc.chunk_count,
                          })}
                        </Text>
                      </View>
                      <View className="flex-row items-center">
                        <MaterialIcons name="access-time" size={14} color="#6b7280" />
                        <Text className="text-xs text-muted-foreground ml-1">
                          {t('schoolManagement.documentsSection.dateLabel', {
                            date: new Date(doc.created_at).toLocaleDateString(),
                          })}
                        </Text>
                      </View>
                    </View>

                    {/* Processing Status */}
                    <View
                      className={`px-2 py-1 rounded self-start ${doc.processed ? 'bg-green-100' : 'bg-yellow-100'
                        }`}
                    >
                      <Text
                        className={`text-xs font-semibold ${doc.processed ? 'text-green-700' : 'text-yellow-700'
                          }`}
                      >
                        {doc.processed
                          ? `✓ ${t('schoolManagement.documentsSection.processed')}`
                          : `⏳ ${t('schoolManagement.documentsSection.processing')}`}
                      </Text>
                    </View>
                  </View>

                  {/* Delete Button */}
                  {isSchoolAdmin(user) && (
                    <TouchableOpacity
                      onPress={() => handleDeleteDocument(doc.id, doc.original_filename)}
                      disabled={deletingDocumentId === doc.id}
                      className="ml-3 p-2 rounded-lg bg-red-50"
                    >
                      {deletingDocumentId === doc.id ? (
                        <ActivityIndicator size="small" color="#DC2626" />
                      ) : (
                        <MaterialIcons name="delete" size={20} color="#DC2626" />
                      )}
                    </TouchableOpacity>
                  )}
                </View>
              </CardContent>
            </Card>
          ))}
        </View>
      )}

      {/* Storage Info Footer */}
      {documents.length > 0 && (
        <Card className="mt-4">
          <CardContent className="p-3">
            <View className="flex-row items-center justify-between">
              <Text className="text-xs text-muted-foreground">
                {t('schoolManagement.documentsSection.totalDocs', { count: documents.length })}
              </Text>
              <Text className="text-xs text-muted-foreground">
                {t('schoolManagement.documentsSection.totalChunks', {
                  count: documents.reduce((sum, doc) => sum + doc.chunk_count, 0),
                })}
              </Text>
              <Text className="text-xs text-muted-foreground">
                {t('schoolManagement.documentsSection.totalSize', {
                  size: formatFileSize(
                    documents.reduce((sum, doc) => sum + doc.file_size, 0)
                  ),
                })}
              </Text>
            </View>
          </CardContent>
        </Card>
      )}
    </View>
  );
}
